import { Card, Row, Col, Typography, Spin, Button, Modal, Space, Tooltip, Switch } from "antd";
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend, CategoryScale, LinearScale, BarElement, Title as ChartTitle } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar, Pie } from "react-chartjs-2";
import { useState, useRef } from "react";
import { FaArrowLeft, FaExpand, FaDownload } from "react-icons/fa";
import { ExpandOutlined, BarChartOutlined, PieChartOutlined, PictureOutlined, FileExcelOutlined } from "@ant-design/icons";

ChartJS.register(ArcElement, ChartTooltip, Legend, CategoryScale, LinearScale, BarElement, ChartDataLabels, ChartTitle);
const { Title } = Typography;
const colors = ["#0968C6", "#00b418ff", "#c61f09ff", "#B4B4B4", "#2b77c3ff", "#347cc4", "#82b0deff", "#347cc4",];

function generateColor(i, total) {
  const hue = (i * (360 / total)) % 360;
  return `hsl(${hue}, 70%, 55%)`;
}

const ESTADOS_BASE = [
  { estado_id: 1, estado_nombre: "En Proceso", total: 0 },
  { estado_id: 2, estado_nombre: "Verificados", total: 0 },
  { estado_id: 3, estado_nombre: "No Continúa", total: 0 },
  { estado_id: 4, estado_nombre: "No Verificados", total: 0 }
];

function EstadoChartModerno({ datosEstados = [], subestadosPorEstado = [], onExpand, onExport }) {
  const [chartType, setChartType] = useState('bar');
  const [viewOption, setViewOption] = useState('count');
  const [selectedEstado, setSelectedEstado] = useState(null);
  const [hiddenIndexes, setHiddenIndexes] = useState([]);
  const chartRef = useRef(null);

  const estadosParaGrafica = ESTADOS_BASE.map(estadoBase => {
    const estadoBackend = datosEstados.find(e => e.estado_id === estadoBase.estado_id);
    return estadoBackend
      ? { ...estadoBase, total: estadoBackend.total }
      : estadoBase;
  });

  const totalEstados = estadosParaGrafica.reduce((sum, est) => sum + est.total, 0);
  const subestadosFiltrados = selectedEstado && subestadosPorEstado
    ? subestadosPorEstado.filter(s => s.estado_id === selectedEstado)
    : [];

  const totalSubestados = subestadosFiltrados.reduce((sum, sub) => sum + sub.total, 0);
  const mostrarSubestados = selectedEstado && subestadosFiltrados.length > 0;
  const datosAMostrar = mostrarSubestados ? subestadosFiltrados : estadosParaGrafica;
  const totalGeneral = mostrarSubestados ? totalSubestados : totalEstados;

  const getChartData = () => ({
    labels: datosAMostrar.map(item =>
      mostrarSubestados ? item.subestado_nombre : item.estado_nombre
    ),
    datasets: [{
      label: viewOption === 'count' ? 'Cantidad' : 'Porcentaje (%)',
      data: datosAMostrar.map((item, i) =>
        hiddenIndexes.includes(i)
          ? null
          : viewOption === 'count'
            ? item.total
            : totalGeneral > 0
              ? ((item.total / totalGeneral) * 100).toFixed(1)
              : 0
      ),
      backgroundColor: datosAMostrar.map((_, i) => colors[i % colors.length]),
      borderRadius: 8,
      borderWidth: 1
    }]
  });

  const getChartOptions = (isPie = false) => {
    const total = totalGeneral;
    const items = datosAMostrar;
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: isPie ? 'right' : 'bottom',
          labels: {
            usePointStyle: true,
            padding: 15,
            boxWidth: 12,
            font: { size: 11 },
            generateLabels: (chart) => {
              const dataset = chart.data.datasets[0];
              return chart.data.labels.map((label, i) => {
                const value = items[i]?.total ?? 0;
                const porcentaje = total > 0
                  ? ((value / total) * 100).toFixed(1)
                  : 0;
                const hidden = hiddenIndexes.includes(i);
                return {
                  text: hidden
                    ? `̶${label}: ${value} (${porcentaje}%)̶`
                    : `${label}: ${value} (${porcentaje}%)`,
                  fillStyle: dataset.backgroundColor[i],
                  hidden,
                  index: i
                };
              });
            }
          },
          onClick: (e, legendItem) => {
            const index = legendItem.index;
            setHiddenIndexes(prev =>
              prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
            );
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              if (context.raw === null) return null;
              const value = context.raw;
              const porcentaje = total > 0
                ? ((value / total) * 100).toFixed(1)
                : 0;
              return `${context.label}: ${value} (${porcentaje}%)`;
            }
          }
        },
        datalabels: {
          display: !isPie,
          anchor: 'end',
          align: 'end',
          font: { size: 11, weight: 'bold' },
          formatter: (value) => {
            if (value === null) return null;
            const porcentaje = total > 0
              ? ((value / total) * 100).toFixed(1)
              : 0;
            return `${value} (${porcentaje}%)`;
          }
        }
      }
    };
    if (isPie) return baseOptions;
    return {
      ...baseOptions,
      scales: {
        y: {
          beginAtZero: true,
          grace: '15%',
          ticks: {
            callback: (v) =>
              viewOption === 'percentage' ? `${v}%` : v
          }
        }
      }
    };
  };

  const exportHighQualityImage = () => {
    if (!chartRef.current) return;
    const canvas = chartRef.current.canvas;
    const exportCanvas = document.createElement('canvas');
    const ctx = exportCanvas.getContext('2d');
    exportCanvas.width = canvas.width * 2;
    exportCanvas.height = canvas.height * 2;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    ctx.scale(2, 2);
    ctx.drawImage(canvas, 0, 0);
    const image = exportCanvas.toDataURL('image/png', 1.0);
    const link = document.createElement('a');
    link.href = image;
    link.download = `estados_verificacion_${new Date().toISOString().split('T')[0]}.png`;
    link.click();
  };

  const handleExportCSV = () => {
    const headers = mostrarSubestados
      ? ['ID Subestado', 'Nombre Subestado', 'Cantidad', 'Porcentaje']
      : ['ID Estado', 'Nombre Estado', 'Cantidad', 'Porcentaje'];
    const rows = datosAMostrar.map(item => {
      const porcentaje = totalGeneral > 0
        ? ((item.total / totalGeneral) * 100).toFixed(2)
        : 0;
      return [
        item.estado_id || item.subestado_id,
        mostrarSubestados ? item.subestado_nombre : item.estado_nombre,
        item.total,
        `${porcentaje}%`
      ];
    });
    rows.push(['', 'TOTAL', totalGeneral, '100%']);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `estados_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const getChartTitle = () => {
    if (mostrarSubestados) {
      const estado = estadosParaGrafica.find(e => e.estado_id === selectedEstado);
      return `Subestados de ${estado?.estado_nombre || 'Estado'}`;
    }
    return "Estado de Verificación";
  };
  return (
    <Card
      hoverable
      style={{ borderRadius: 12 }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 16,
        flexWrap: 'wrap',
        gap: 16
      }}>
        <div>
          <Title level={4} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            {mostrarSubestados && (
              <Button
                size="small"
                onClick={() => setSelectedEstado(null)}
                style={{ marginRight: 8 }}
              >
                ← Volver
              </Button>
            )}
            {getChartTitle()}
          </Title>
          <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>
            {`${totalGeneral} ${mostrarSubestados ? 'subestados' : 'estados'} totales`}
          </div>
        </div>
        <Space wrap>
          <Tooltip title={viewOption === 'count' ? "Mostrar porcentajes" : "Mostrar cantidades"}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 12px',
              backgroundColor: '#f8f9fa',
              borderRadius: 6,
              border: '1px solid #dee2e6'
            }}>
              <span style={{
                fontSize: 13,
                color: viewOption === 'count' ? '#024589ff' : '#6c757d',
                fontWeight: viewOption === 'count' ? '600' : 'normal'
              }}>
                Cantidad
              </span>
              <Switch
                checked={viewOption === 'percentage'}
                onChange={(checked) => setViewOption(checked ? 'percentage' : 'count')}
                size="small"
              />
              <span style={{
                fontSize: 13,
                color: viewOption === 'percentage' ? '#024589ff' : '#6c757d',
                fontWeight: viewOption === 'percentage' ? '600' : 'normal'
              }}>
                Porcentaje
              </span>
            </div>
          </Tooltip>
          <div style={{ display: 'flex', gap: 8 }}>
            <Tooltip title="Cambiar tipo de gráfico">
              <Button
                icon={chartType === 'bar' ? <PieChartOutlined /> : <BarChartOutlined />}
                onClick={() => setChartType(chartType === 'bar' ? 'pie' : 'bar')}
                size="small"
                type={chartType === 'bar' ? 'default' : 'dashed'}
              >
                {chartType === 'bar' ? 'Pastel' : 'Barras'}
              </Button>
            </Tooltip>
            <Tooltip title="Exportar imagen del gráfico (alta calidad)">
              <Button
                icon={<PictureOutlined />}
                onClick={exportHighQualityImage}
                size="small"
                type="primary"
              >
                Imagen
              </Button>
            </Tooltip>
            <Tooltip title="Exportar datos en formato CSV">
              <Button
                icon={<FileExcelOutlined />}
                onClick={handleExportCSV}
                size="small"
              >
                CSV
              </Button>
            </Tooltip>
            <Tooltip title="Maximizar">
              <Button
                icon={<ExpandOutlined />}
                onClick={onExpand}
                size="small"
                type="default"
              />
            </Tooltip>
          </div>
        </Space>
      </div>
      <div style={{ height: 400, backgroundColor: '#FFFFFF' }}>
        {chartType === 'bar' ? (
          <Bar
            ref={chartRef}
            data={getChartData()}
            options={getChartOptions(false)}
          />
        ) : (
          <Pie
            ref={chartRef}
            data={getChartData()}
            options={getChartOptions(true)}
          />
        )}
      </div>
    </Card>
  );
}

function GeneroChartModerno({ datosGeneros = [], onExpand, onExport }) {
  const [chartType, setChartType] = useState('bar');
  const [viewOption, setViewOption] = useState('count');
  const [hiddenIndexes, setHiddenIndexes] = useState([]);
  const chartRef = useRef(null);
  const totalGeneros = datosGeneros.reduce((sum, gen) => sum + gen.total, 0);
  const generosProcesados = [];
  if (datosGeneros && Array.isArray(datosGeneros) && datosGeneros.length > 0) {
    const masculino = datosGeneros.find(gen =>
      gen.genero_nombre?.toLowerCase().includes('masculino') ||
      gen.genero?.toLowerCase().includes('m')
    );
    const femenino = datosGeneros.find(gen =>
      gen.genero_nombre?.toLowerCase().includes('femenino') ||
      gen.genero?.toLowerCase().includes('f')
    );
    if (masculino) generosProcesados.push({
      label: "Masculino",
      total: masculino.total,
      color: "#347cc4"
    });
    if (femenino) generosProcesados.push({
      label: "Femenino",
      total: femenino.total,
      color: "#29cdd8"
    });
    const otrosGeneros = datosGeneros.filter(gen =>
      !gen.genero_nombre?.toLowerCase().includes('masculino') &&
      !gen.genero_nombre?.toLowerCase().includes('femenino') &&
      !gen.genero?.toLowerCase().includes('m') &&
      !gen.genero?.toLowerCase().includes('f')
    );
    otrosGeneros.forEach((gen, index) => {
      generosProcesados.push({
        label: gen.genero_nombre || 'Otro',
        total: gen.total,
        color: colors[(index + 2) % colors.length]
      });
    });
  }

  const getChartData = () => {
    const labels = generosProcesados.map(g => g.label);
    const dataValues = generosProcesados.map((g, i) => {
      if (hiddenIndexes.includes(i)) return null;
      if (viewOption === 'count') {
        return g.total;
      } else {
        return totalGeneros > 0
          ? ((g.total / totalGeneros) * 100).toFixed(1)
          : 0;
      }
    });
    return {
      labels,
      datasets: [{
        label: viewOption === 'count' ? 'Cantidad' : 'Porcentaje (%)',
        data: dataValues,
        backgroundColor: generosProcesados.map(g => g.color),
        borderRadius: 8,
        borderWidth: 1
      }]
    };
  };

  const getChartOptions = (isPie = false) => {
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: isPie ? 'right' : 'bottom',
          labels: {
            usePointStyle: true,
            padding: isPie ? 10 : 20,
            boxWidth: 12,
            font: { size: 11 },
            generateLabels: (chart) => {
              const dataset = chart.data.datasets[0];
              return chart.data.labels.map((label, i) => {
                const value = generosProcesados[i]?.total ?? 0;
                const porcentaje = totalGeneros > 0
                  ? ((value / totalGeneros) * 100).toFixed(1)
                  : 0;
                const hidden = hiddenIndexes.includes(i);
                return {
                  text: hidden
                    ? `̶${label}: ${value} (${porcentaje}%)̶`
                    : `${label}: ${value} (${porcentaje}%)`,
                  fillStyle: dataset.backgroundColor[i],
                  hidden,
                  index: i
                };
              });
            }
          },
          onClick: (e, legendItem, legend) => {
            const index = legendItem.index;
            setHiddenIndexes(prev => {
              if (prev.includes(index)) {
                return prev.filter(i => i !== index);
              } else {
                return [...prev, index];
              }
            });
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              if (context.raw === null) return null;
              const value = Number(context.raw);
              const porcentaje = totalGeneros > 0
                ? ((value / totalGeneros) * 100).toFixed(1)
                : 0;
              return viewOption === 'count'
                ? `${context.label}: ${value} (${porcentaje}%)`
                : `${context.label}: ${value}%`;
            }
          }
        },
        datalabels: {
          display: !isPie,
          anchor: 'end',
          align: 'end',
          font: { size: 11, weight: 'bold' },
          formatter: (value) => {
            if (value === null) return null;
            const porcentaje = totalGeneros > 0
              ? ((value / totalGeneros) * 100).toFixed(1)
              : 0;
            return viewOption === 'count'
              ? `${value} (${porcentaje}%)`
              : `${value}%`;
          }
        }
      }
    };
    if (isPie) {
      return baseOptions;
    }
    return {
      ...baseOptions,
      scales: {
        y: {
          beginAtZero: true,
          grace: '15%',
          ticks: {
            callback: function (value) {
              if (viewOption === 'percentage') return value + '%';
              return value;
            }
          }
        }
      }
    };
  };


  const exportHighQualityImage = () => {
    if (!chartRef.current) return;
    const canvas = chartRef.current.canvas;
    const exportCanvas = document.createElement('canvas');
    const ctx = exportCanvas.getContext('2d');
    exportCanvas.width = canvas.width * 2;
    exportCanvas.height = canvas.height * 2;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    ctx.scale(2, 2);
    ctx.drawImage(canvas, 0, 0);
    const image = exportCanvas.toDataURL('image/png', 1.0);
    const link = document.createElement('a');
    link.href = image;
    link.download = `distribucion_genero_${new Date().toISOString().split('T')[0]}.png`;
    link.click();
  };

  const handleExportCSV = () => {
    const headers = ['Género', 'Cantidad', 'Porcentaje'];
    const rows = generosProcesados.map(g => {
      const porcentaje = totalGeneros > 0
        ? ((g.total / totalGeneros) * 100).toFixed(2)
        : 0;
      return [g.label, g.total, `${porcentaje}%`];
    });
    rows.push(['TOTAL', totalGeneros, '100%']);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `generos_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card
      hoverable
      style={{ borderRadius: 12 }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 16,
        flexWrap: 'wrap',
        gap: 16
      }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Distribución por Género
          </Title>
          <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>
            {`${totalGeneros} participantes totales`}
          </div>
        </div>
        <Space wrap>
          <Tooltip title={viewOption === 'count' ? "Mostrar porcentajes" : "Mostrar cantidades"}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 12px',
              backgroundColor: '#f8f9fa',
              borderRadius: 6,
              border: '1px solid #dee2e6'
            }}>
              <span style={{
                fontSize: 13,
                color: viewOption === 'count' ? '#024589ff' : '#6c757d',
                fontWeight: viewOption === 'count' ? '600' : 'normal'
              }}>
                Cantidad
              </span>
              <Switch
                checked={viewOption === 'percentage'}
                onChange={(checked) => setViewOption(checked ? 'percentage' : 'count')}
                size="small"
              />
              <span style={{
                fontSize: 13,
                color: viewOption === 'percentage' ? '#024589ff' : '#6c757d',
                fontWeight: viewOption === 'percentage' ? '600' : 'normal'
              }}>
                Porcentaje
              </span>
            </div>
          </Tooltip>
          <div style={{ display: 'flex', gap: 8 }}>
            <Tooltip title="Cambiar tipo de gráfico">
              <Button
                icon={chartType === 'bar' ? <PieChartOutlined /> : <BarChartOutlined />}
                onClick={() => setChartType(chartType === 'bar' ? 'pie' : 'bar')}
                size="small"
                type={chartType === 'bar' ? 'default' : 'dashed'}
              >
                {chartType === 'bar' ? 'Pastel' : 'Barras'}
              </Button>
            </Tooltip>
            <Tooltip title="Exportar imagen del gráfico (alta calidad)">
              <Button
                icon={<PictureOutlined />}
                onClick={exportHighQualityImage}
                size="small"
                type="primary"
              >
                Imagen
              </Button>
            </Tooltip>
            <Tooltip title="Exportar datos en formato CSV">
              <Button
                icon={<FileExcelOutlined />}
                onClick={handleExportCSV}
                size="small"
              >
                CSV
              </Button>
            </Tooltip>
            <Tooltip title="Maximizar">
              <Button
                icon={<ExpandOutlined />}
                onClick={onExpand}
                size="small"
                type="default"
              />
            </Tooltip>
          </div>
        </Space>
      </div>
      <div style={{ height: 400, backgroundColor: '#FFFFFF' }}>
        {chartType === 'bar' ? (
          <Bar
            ref={chartRef}
            data={getChartData()}
            options={getChartOptions(false)}
          />
        ) : (
          <Pie
            ref={chartRef}
            data={getChartData()}
            options={getChartOptions(true)}
          />
        )}
      </div>
    </Card>
  );
}
export function ChartsSection({ datosGraficas, estadosValidacion, loading = false }) {
  const [selectedDepto, setSelectedDepto] = useState(null);
  const [expandedChart, setExpandedChart] = useState(null);
  const departamentosChartRef = useRef();
  const expandedDepartamentosRef = useRef();
  if (loading) {
    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 30 }}>
        <Col span={24}>
          <Card>
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
              <p style={{ marginTop: '20px', color: '#666' }}>
                Cargando datos para gráficas...
              </p>
            </div>
          </Card>
        </Col>
      </Row>
    );
  }

  const hasValidData = datosGraficas &&
    Array.isArray(datosGraficas.estados) &&
    Array.isArray(datosGraficas.generos) &&
    Array.isArray(datosGraficas.departamentos);

  const hasRealData = hasValidData && (
    datosGraficas.estados.some(estado => estado.total > 0) ||
    datosGraficas.generos.length > 0 ||
    datosGraficas.departamentos.length > 0
  );

  if (!hasRealData) {
    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 30 }}>
        <Col span={24}>
          <Card>
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <p>No hay datos disponibles para mostrar gráficas</p>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                Los filtros aplicados no devuelven resultados.
              </p>
            </div>
          </Card>
        </Col>
      </Row>
    );
  }

  const { estados, subestadosPorEstado, generos, departamentos, municipiosPorDepartamento } = datosGraficas;

  const departamentosConPorcentajes = departamentos && Array.isArray(departamentos)
    ? departamentos.map(depto => {
      const totalDepartamentos = departamentos.reduce((sum, d) => sum + (d.total || 0), 0);
      const porcentaje = totalDepartamentos > 0
        ? ((depto.total / totalDepartamentos) * 100).toFixed(1)
        : 0;
      return {
        ...depto,
        porcentaje: parseFloat(porcentaje)
      };
    })
    : [];

  const departamentoData = {
    labels: departamentosConPorcentajes.map(depto => depto.departamento),
    datasets: [{
      data: departamentosConPorcentajes.map(depto => depto.total),
      rawData: departamentosConPorcentajes.map(depto => ({
        total: depto.total,
        porcentaje: depto.porcentaje
      })),
      backgroundColor: departamentosConPorcentajes.map((_, i) => generateColor(i, departamentosConPorcentajes.length)),
      borderColor: '#fff',
      borderWidth: 2,
    }],
  };

  const municipiosFiltrados = selectedDepto && municipiosPorDepartamento
    ? municipiosPorDepartamento.filter(m => m.departamento_id === selectedDepto)
    : [];

  const totalMunicipios = municipiosFiltrados.reduce((sum, mun) => sum + mun.total, 0);

  const municipioData = {
    labels: municipiosFiltrados.map(mun => mun.municipio),
    datasets: [{
      data: municipiosFiltrados.map(mun => mun.total),
      rawData: municipiosFiltrados.map(mun => ({
        total: mun.total,
        porcentaje: totalMunicipios > 0 ? ((mun.total / totalMunicipios) * 100).toFixed(1) : 0
      })),
      backgroundColor: municipiosFiltrados.map((_, i) => generateColor(i, municipiosFiltrados.length)),
      borderColor: '#fff',
      borderWidth: 2,
    }],
  };

  const handleDeptoClick = (event, elements) => {
    if (selectedDepto !== null) return;
    if (!elements || !elements.length) return;
    const element = elements[0];
    const index = element.index;
    if (!departamentosConPorcentajes || index < 0 || index >= departamentosConPorcentajes.length) return;
    const departamento = departamentosConPorcentajes[index];
    const tieneMunicipios = municipiosPorDepartamento && municipiosPorDepartamento.some(
      m => m.departamento_id === departamento.departamento_id
    );
    if (tieneMunicipios) {
      setSelectedDepto(departamento.departamento_id);
    }
  };

  const chartOptionsDepartamento = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { display: false },
      y: { display: false },
    },
    plugins: {
      legend: {
        position: "right",
        display: true,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 11,
            family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
          },
          generateLabels: (chart) => {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label, i) => {
                const dataset = data.datasets[0];
                const rawData = dataset.rawData?.[i];
                const value = rawData ? rawData.total : dataset.data[i];
                const porcentaje = rawData ? rawData.porcentaje : dataset.data[i];
                const meta = chart.getDatasetMeta(0);
                const hidden = meta && meta.data[i] ? meta.data[i].hidden : false;
                return {
                  text: hidden
                    ? `̶${label}: ${value} (${porcentaje}%)̶`
                    : `${label}: ${value} (${porcentaje}%)`,
                  fillStyle: dataset.backgroundColor[i],
                  strokeStyle: dataset.borderColor || '#fff',
                  lineWidth: 1,
                  hidden: hidden,
                  index: i,
                  fontStyle: hidden ? 'italic' : 'normal',
                  textDecoration: hidden ? 'line-through' : 'none'
                };
              });
            }
            return [];
          }
        },
        onClick: (e, legendItem, legend) => {
          const index = legendItem.index;
          const chart = legend.chart;
          const meta = chart.getDatasetMeta(0);
          if (meta && meta.data[index]) {
            const hidden = !meta.data[index].hidden;
            meta.data[index].hidden = hidden;
            chart.update();
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const rawData = context.dataset.rawData?.[context.dataIndex];
            if (rawData) {
              return `${context.label}: ${rawData.total} participantes (${rawData.porcentaje}%)`;
            }
            return `${context.label}: ${context.raw}`;
          },
        },
      },
      datalabels: {
        display: false
      },
    },
    radius: "80%",
    onClick: handleDeptoClick,
    cutout: '0%',
  };
  const chartOptionsPieSimple = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { display: false },
      y: { display: false }
    },
    plugins: {
      legend: {
        position: "right",
        display: true,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 11,
            family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
          },
          generateLabels: (chart) => {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label, i) => {
                const dataset = data.datasets[0];
                const rawData = dataset.rawData?.[i];
                const value = rawData ? rawData.total : dataset.data[i];
                const porcentaje = rawData ? rawData.porcentaje : dataset.data[i];
                const meta = chart.getDatasetMeta(0);
                const hidden = meta && meta.data[i] ? meta.data[i].hidden : false;
                return {
                  text: hidden
                    ? `̶${label}: ${value} (${porcentaje}%)̶`
                    : `${label}: ${value} (${porcentaje}%)`,
                  fillStyle: dataset.backgroundColor[i],
                  strokeStyle: dataset.borderColor || '#fff',
                  lineWidth: 1,
                  hidden: hidden,
                  index: i,
                  fontStyle: hidden ? 'italic' : 'normal',
                  textDecoration: hidden ? 'line-through' : 'none'
                };
              });
            }
            return [];
          }
        },
        onClick: (e, legendItem, legend) => {
          const index = legendItem.index;
          const chart = legend.chart;
          const meta = chart.getDatasetMeta(0);
          if (meta && meta.data[index]) {
            const hidden = !meta.data[index].hidden;
            meta.data[index].hidden = hidden;
            chart.update();
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const rawData = context.dataset.rawData?.[context.dataIndex];
            if (rawData) {
              return `${context.label}: ${rawData.total} participantes (${rawData.porcentaje}%)`;
            }
            return `${context.label}: ${context.raw}`;
          }
        }
      },
      datalabels: {
        display: false
      }
    },
    radius: "80%",
  };

  const exportDepartamentosChart = (isExpanded = false) => {
    const chartInstance = isExpanded ? expandedDepartamentosRef.current : departamentosChartRef.current;
    if (!chartInstance) {
      console.error('No se encontró la instancia de la gráfica de departamentos');
      return;
    }
    try {
      const canvas = chartInstance.canvas;
      if (!canvas) {
        console.error('No se pudo obtener el canvas de la gráfica');
        return;
      }
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      tempCtx.fillStyle = '#ffffff';
      tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
      tempCtx.drawImage(canvas, 0, 0);
      const link = document.createElement('a');
      const timestamp = new Date().getTime();
      link.download = `grafica-departamentos-${timestamp}.png`;
      link.href = tempCanvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error al exportar la gráfica:', error);
    }
  };
  const renderExpandedDepartamentos = () => {
    if (expandedChart !== 'departamentos') return null;
    const chartData = !selectedDepto ? departamentoData : municipioData;
    const chartOptions = !selectedDepto ? chartOptionsDepartamento : { ...chartOptionsPieSimple, onClick: undefined };
    const chartTitle = !selectedDepto ? "Participantes por Departamento" : `Municipios de ${departamentosConPorcentajes?.find(d => d.departamento_id === selectedDepto)?.departamento || 'Departamento'}`;
    return (
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {selectedDepto && (
              <div title="Volver a departamentos" style={{ display: 'inline-block' }}>
                <FaArrowLeft
                  style={{ cursor: "pointer" }}
                  onClick={() => setSelectedDepto(null)}
                />
              </div>
            )}
            <span>{chartTitle}</span>
          </div>
        }
        open={!!expandedChart && expandedChart === 'departamentos'}
        onCancel={() => setExpandedChart(null)}
        footer={[
          <Button key="export" onClick={() => exportDepartamentosChart(true)}>
            <FaDownload /> Exportar
          </Button>,
          <Button key="close" onClick={() => setExpandedChart(null)}>
            Cerrar
          </Button>
        ]}
        width="90%"
        style={{ top: 20 }}
        destroyOnHidden
      >
        <div style={{ position: 'relative', height: '60vh', width: '100%' }}>
          <Pie
            ref={expandedDepartamentosRef}
            data={chartData}
            options={chartOptions}
            key={`expanded-departamentos-${selectedDepto ? 'municipios' : 'departamentos'}`}
          />
        </div>
      </Modal>
    );
  };
  const renderExpandedGenero = () => {
    if (expandedChart !== 'genero') return null;
    return (
      <Modal
        title="Distribución por Género"
        open={expandedChart === 'genero'}
        onCancel={() => setExpandedChart(null)}
        footer={null}
        width="90%"
        style={{ top: 20 }}
        destroyOnHidden
      >
        <div style={{ height: '65vh' }}>
          <GeneroChartModerno
            datosGeneros={generos}
          />
        </div>
      </Modal>
    );
  };

  const renderExpandedEstados = () => {
    if (expandedChart !== 'estados') return null;
    return (
      <Modal
        title="Estado de Verificación"
        open={expandedChart === 'estados'}
        onCancel={() => setExpandedChart(null)}
        footer={null}
        width="90%"
        style={{ top: 20 }}
        destroyOnHidden
      >
        <div style={{ height: '65vh' }}>
          <EstadoChartModerno
            datosEstados={estados}
            subestadosPorEstado={subestadosPorEstado}
          />
        </div>
      </Modal>
    );
  };

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 30 }}>
        <Col xs={24} md={12} lg={8}>
          <EstadoChartModerno
            datosEstados={estados}
            subestadosPorEstado={subestadosPorEstado}
            onExpand={() => setExpandedChart('estados')}
            onExport={() => exportDepartamentosChart(false)}
          />
        </Col>
        <Col xs={24} md={12} lg={8}>
          <GeneroChartModerno
            datosGeneros={generos}
            onExpand={() => setExpandedChart('genero')}
            onExport={() => exportDepartamentosChart(false)}
          />
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Card
            hoverable
            actions={[
              <div title="Maximizar gráfica" key="expand" style={{ display: 'inline-block' }}>
                <FaExpand onClick={() => setExpandedChart('departamentos')} style={{ cursor: 'pointer', fontSize: '16px' }} />
              </div>,
              <div title="Exportar como imagen" key="download" style={{ display: 'inline-block' }}>
                <FaDownload onClick={() => exportDepartamentosChart(false)} style={{ cursor: 'pointer', fontSize: '16px' }} />
              </div>
            ]}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {selectedDepto && (
                <div title="Volver a departamentos" style={{ display: 'inline-block' }}>
                  <FaArrowLeft
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedDepto(null)}
                  />
                </div>
              )}
              <Title level={5}>
                {selectedDepto
                  ? `Municipios de ${departamentosConPorcentajes?.find(d => d.departamento_id === selectedDepto)?.departamento || 'Departamento'}`
                  : "Participantes por Departamento"}
              </Title>
            </div>
            <div style={{ height: 470, position: 'relative' }}>
              {!selectedDepto ? (
                <Pie
                  ref={departamentosChartRef}
                  data={departamentoData}
                  options={chartOptionsDepartamento}
                />
              ) : (
                <Pie
                  ref={departamentosChartRef}
                  data={municipioData}
                  options={{
                    ...chartOptionsPieSimple,
                    onClick: undefined
                  }}
                />
              )}
            </div>
          </Card>
        </Col>
      </Row>
      {renderExpandedDepartamentos()}
      {renderExpandedGenero()}
      {renderExpandedEstados()}
    </>
  );
}